import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import dayjs from "dayjs";
import { Check, CheckCircle2, Copy, Download, Eye, FileText, Link2, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  ProductionPdfPreview,
  type ProductionAttachment,
  type ProductionPdfProps,
} from "./ProductionPdfPreview";
import { Button } from "@/components/Button/Button";

export type ProductionSigningLink = {
  name: string;
  contactId: string;
  link: string;
  status: string;
};

export type ProductionSigningResultData = {
  taskId: string;
  title: string;
  createDate: string;
  attachment: ProductionAttachment | null;
  signingLinks: ProductionSigningLink[];
};

type Props = {
  result: ProductionSigningResultData;
  fetchAttachment: ProductionPdfProps["fetchAttachment"];
  onDownload: (taskId: string, name: string) => void;
};

export function ProductionSigningResult({
  result,
  fetchAttachment,
  onDownload,
}: Props) {
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const created = dayjs(result.createDate);

  const copySigningLink = async (signer: ProductionSigningLink) => {
    try {
      await navigator.clipboard.writeText(signer.link);
      setCopiedLink(signer.link);
    } catch {
      toast.error("Kunne ikke kopiere signeringslenken.");
    }
  };

  return (
    <section
      aria-label="Resultat av signeringsproduksjon"
      className="flex h-full min-h-0 flex-col overflow-y-auto bg-muted/20"
    >
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-5 border-b bg-gradient-to-br from-emerald-50/80 via-card to-card px-6 py-7 dark:from-emerald-950/30">
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 ring-4 ring-emerald-50 dark:bg-emerald-900/40 dark:ring-emerald-950/30">
          <CheckCircle2
            aria-hidden="true"
            className="size-6 text-emerald-600 dark:text-emerald-400"
          />
          </span>
          <div className="min-w-0">
            <p role="status" className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100/80 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
              <Check aria-hidden="true" className="size-3" />
              Sendt til signering
            </p>
            <h2 className="text-xl font-semibold tracking-tight">
              Produksjonen er ferdig
            </h2>
            <p
              className="mt-1 max-w-lg truncate text-xs text-muted-foreground"
              title={result.title}
            >
              {result.title}
            </p>
          </div>
        </div>
        {result.attachment && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-lg normal-case"
              icon={<Eye aria-hidden="true" className="size-4" />}
              onClick={() => setPreviewOpen(true)}
            >
              Forhåndsvis PDF
            </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-lg normal-case"
            icon={<Download aria-hidden="true" className="size-4" />}
            onClick={() => {
              if (result.attachment) {
                onDownload(result.attachment.taskId, result.attachment.name);
              }
            }}
          >
            Last ned PDF
          </Button>
          </div>
        )}
      </header>

      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-start gap-6 p-4 md:p-6 lg:grid-cols-[minmax(0,1fr)_17rem]">
          <section
            aria-label="Signeringslenker"
            className="min-w-0"
          >
            <div className="mb-2.5 flex items-center gap-2">
              <Link2 aria-hidden="true" className="size-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Signeringslenker</h3>
              <span className="text-xs tabular-nums text-muted-foreground">
                ({result.signingLinks.length})
              </span>
            </div>
            <p role="status" aria-live="polite" className="sr-only">
              {copiedLink
                ? `Signeringslenken for ${result.signingLinks.find((signer) => signer.link === copiedLink)?.name ?? "signataren"} er kopiert.`
                : ""}
            </p>
            {result.signingLinks.length ? (
              <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
                <ul className="divide-y divide-border/60">
                {result.signingLinks.map((signer) => (
                  <li
                    key={`${signer.contactId}-${signer.link}`}
                    className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/20"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <h4 className="break-words text-xs font-semibold">
                          {signer.name}
                        </h4>
                        <span className="text-[10px] text-muted-foreground">
                          {signer.contactId}
                        </span>
                        <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current opacity-60" />
                          <span className="break-words">{signer.status || "Ukjent status"}</span>
                        </span>
                      </div>
                      <input
                        aria-label={`Signeringslenke for ${signer.name}`}
                        readOnly
                        value={signer.link}
                        title={signer.link}
                        onFocus={(event) => event.currentTarget.select()}
                        className="mt-1 block w-full min-w-0 rounded-md border border-transparent bg-transparent py-1 font-mono text-[11px] text-muted-foreground hover:border-input focus:border-input focus:bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>
                    <Button
                      size="xs"
                      variant="outline"
                      type="button"
                      className={`shrink-0 rounded-lg normal-case ${
                        copiedLink === signer.link
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                          : ""
                      }`}
                      disabled={!signer.link}
                      aria-label={`Kopier signeringslenke for ${signer.name}`}
                      onClick={() => void copySigningLink(signer)}
                      icon={
                        copiedLink === signer.link ? (
                          <Check className="size-3" />
                        ) : (
                          <Copy className="size-3" />
                        )
                      }
                    >
                      {copiedLink === signer.link ? "Kopiert" : "Kopier"}
                    </Button>
                  </li>
                ))}
                </ul>
              </div>
            ) : (
              <p role="status" className="rounded-xl border border-dashed bg-card p-5 text-sm text-muted-foreground">
                Ingen signeringslenker ble funnet i tasken. Dette bekrefter ikke
                at signeringslenker er sendt.
              </p>
            )}
          </section>
          <section
            aria-label="Produksjonsinformasjon"
            className="min-w-0 rounded-2xl border bg-card p-5"
          >
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold">
              <FileText aria-hidden="true" className="size-4 text-muted-foreground" />
              Produksjonsinformasjon
            </h3>
            <dl className="space-y-4 text-xs [&_dd]:mt-1">
              <div>
                <dt className="text-muted-foreground">Dokument</dt>
                <dd className="break-words">{result.attachment?.name ?? "Ingen PDF funnet"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Task-ID</dt>
                <dd className="break-all font-mono">{result.taskId}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Opprettet</dt>
                <dd>
                  {created.isValid()
                    ? created.format("DD.MM.YYYY HH:mm")
                    : "Ukjent"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Signatarer med lenke</dt>
                <dd>{result.signingLinks.length}</dd>
              </div>
            </dl>
            <p className="mt-5 border-t pt-4 text-[11px] leading-relaxed text-muted-foreground">
              Statusene viser informasjonen da tasken ble hentet, og oppdateres
              ikke automatisk.
            </p>
            {!result.attachment && (
              <p role="alert" className="mt-3 text-xs text-destructive">
                Ingen PDF-fil ble funnet i produksjonen.
              </p>
            )}
          </section>
      </div>
      {previewOpen && result.attachment && (
        <Dialog
          open
          onClose={() => setPreviewOpen(false)}
          maxWidth="lg"
          fullWidth
          aria-labelledby="signing-result-preview-title"
          slotProps={{ paper: { sx: { height: "85vh", borderRadius: 3 } } }}
        >
          <DialogTitle
            id="signing-result-preview-title"
            className="flex items-center justify-between gap-3 border-b"
          >
            <span className="min-w-0 truncate text-base" title={result.attachment.name}>
              {result.attachment.name}
            </span>
            <Button
              type="button"
              variant="icon"
              size="sm"
              aria-label="Lukk forhåndsvisning"
              onClick={() => setPreviewOpen(false)}
            >
              <X aria-hidden="true" className="size-4" />
            </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <ProductionPdfPreview
              attachment={result.attachment}
              fetchAttachment={fetchAttachment}
            />
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
